/**
 * Variant-specific string overrides.
 *
 * Each key in `overrides` maps a variant name to a record of i18n keys
 * whose values should replace the default translations for that variant.
 *
 * Detection is hostname-based (same pattern as cmsHost.ts).
 */

type VariantId = "thoiry" | "default";

const VARIANT_DETECTORS: { id: VariantId; match: (hostname: string) => boolean }[] = [
    { id: "thoiry", match: (h) => h.includes("thoiry") || h.includes("wowgroupe.rewindstories") },
];

/** Resolve which variant the CMS is currently running as. */
export function getCurrentVariant(): VariantId {
    if (typeof window === "undefined") return "default";
    const hostname = window.location.hostname;
    for (const detector of VARIANT_DETECTORS) {
        if (detector.match(hostname)) return detector.id;
    }
    return "default";
}

/**
 * Per-variant overrides keyed by i18n translation key.
 * Only keys that differ from the default need to be listed.
 *
 * "default" keeps the original translation file values — no overrides.
 * "thoiry" gets the new wording for step 2 (categories).
 */
const overrides: Record<VariantId, Record<string, Record<string, string>>> = {
    default: {
        // Other variants keep the default translation file values — no overrides
    },
    thoiry: {
        fr: {
            transportModeTitle: "Catégories in-app",
            transportModeSubtitle:
                "Dans quelle catégorie souhaitez-vous voir apparaître votre expérience au sein de l'application ?",
            transportModeDescription: "Votre expérience apparaîtra dans la catégorie choisie.",
            selectTransportMode: "Sélectionnez une catégorie",
            transportModeWalkOutdoor: "Visites",
            transportModeWalkIndoor: "Animations et lieux à découvrir",
            transportModeCar: "Quêtes",
        },
        en: {
            transportModeTitle: "In-app categories",
            transportModeSubtitle: "In which category would you like your experience to appear within the app?",
            transportModeDescription: "Your experience will appear in the chosen category.",
            selectTransportMode: "Select a category",
            transportModeWalkOutdoor: "Visits",
            transportModeWalkIndoor: "Animations and places to discover",
            transportModeCar: "Quests",
        },
        pt: {
            transportModeTitle: "Categorias in-app",
            transportModeSubtitle: "Em que categoria gostaria que a sua experiência apareça na aplicação?",
            transportModeDescription: "A sua experiência aparecerá na categoria escolhida.",
            selectTransportMode: "Selecione uma categoria",
            transportModeWalkOutdoor: "Visitas",
            transportModeWalkIndoor: "Animações e locais a descobrir",
            transportModeCar: "Missões",
        },
        es: {
            transportModeTitle: "Categorías in-app",
            transportModeSubtitle: "¿En qué categoría le gustaría que aparezca su experiencia en la aplicación?",
            transportModeDescription: "Su experiencia aparecerá en la categoría elegida.",
            selectTransportMode: "Seleccione una categoría",
            transportModeWalkOutdoor: "Visitas",
            transportModeWalkIndoor: "Animaciones y lugares por descubrir",
            transportModeCar: "Misiones",
        },
    },
};

/**
 * Return the override value for a given key, or `undefined` if the default
 * translation should be used.
 *
 * @param key  - i18n translation key
 * @param lang - current UI language code (e.g. "fr", "en")
 */
export function getVariantOverride(key: string, lang: string): string | undefined {
    const variant = getCurrentVariant();
    const langLower = lang.toLowerCase().slice(0, 2);
    return overrides[variant]?.[langLower]?.[key];
}
